import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLocation } from 'react-router-dom';
import { useMobile } from '../hooks/useMobile';
import Layout from './Layout';
import TemplatesPage from '../pages/TemplatesPage';
import DiscoverPage from '../pages/DiscoverPage';
import ExploreDetailPage from '../pages/ExploreDetailPage';
import SavedPage from '../pages/SavedPage';
import SavedDetailPage from '../pages/SavedDetailPage';
import ExploreImagesPage from '../pages/ExploreImagesPage';
import ProjectsPage from '../pages/ProjectsPage';
import ProjectDetailPage from '../pages/ProjectDetailPage';
import CommunityDetectionDetailPage from '../pages/CommunityDetectionDetailPage';

const AnimatedRoutes = () => {
  const location = useLocation();
  const { isMobile } = useMobile();
  

  // Route detection
  const isExploreImages = location.pathname.includes('/images') && location.pathname.startsWith('/starters/');
  const isExploreDetail = location.pathname.startsWith('/starters/') && location.pathname !== '/starters' && !isExploreImages;
  const isExplore = location.pathname === '/starters';
  const isSavedDetail = location.pathname.startsWith('/saved/') && location.pathname !== '/saved';
  const isSaved = location.pathname === '/saved';
  // Projects routes - exclude sub-routes like /chats, /images
  const isProjectsImages = location.pathname.includes('/images') && location.pathname.startsWith('/projects/');
  const isProjectsDetail = location.pathname.startsWith('/projects/') && 
                           location.pathname !== '/projects' && 
                           !isProjectsImages &&
                           !location.pathname.includes('/chats/');
  const isProjects = location.pathname === '/projects' || location.pathname === '/projects/';
  const isCommunityList = location.pathname === '/discover' || location.pathname === '/discover/';
  const isObservationDetail = location.pathname.startsWith('/discover/observation/');
  const isCommunityDetail = location.pathname.startsWith('/discover/') && !isCommunityList && !isObservationDetail;
  const isCommunity = isCommunityList;
  const isCommunityRoute = isCommunityList || isCommunityDetail || isObservationDetail;
  
  // Determine which component to render
  const getComponent = () => {
    if (isExploreImages) {
      return <ExploreImagesPage />;
    }
    if (isExploreDetail) {
      return <ExploreDetailPage />;
    }
    if (isExplore) {
      return <TemplatesPage />;
    }
    if (isObservationDetail) {
      // For observation/detection detail pages
      return <CommunityDetectionDetailPage />;
    }
    if (isCommunityDetail) {
      // For discover detail pages, use ProjectDetailPage since we're now showing public projects
      return <ProjectDetailPage />;
    }
    if (isCommunity) {
      return <DiscoverPage />;
    }
    if (isSavedDetail) {
      return <SavedDetailPage />;
    }
    if (isSaved) {
      return <SavedPage />;
    }
    if (isProjectsDetail) {
      return <ProjectDetailPage />;
    }
    if (isProjects) {
      return <ProjectsPage />;
    }
    return null;
  };

  // Animation variants
  const getAnimationVariant = () => {
    if (!isMobile) return null;

    const transitionDirection = location.state?.transitionDirection;
    const previousPath = location.state?.previousPath;
    
    // Check for detail-to-detail transitions even without explicit transition state
    const previousIsExploreDetail = previousPath?.startsWith('/starters/') && previousPath !== '/starters';
    const previousIsSavedDetail = previousPath?.startsWith('/saved/') && previousPath !== '/saved';
    const isDetailToDetailCrossSection = (isExploreDetail || isSavedDetail) && 
                                         (previousIsExploreDetail || previousIsSavedDetail) &&
                                         ((isExploreDetail && previousIsSavedDetail) || (isSavedDetail && previousIsExploreDetail));

    // console.log('🔍 DETECTION LOGIC:', {
    //   current: location.pathname,
    //   previousPath,
    //   isExploreDetail,
    //   isSavedDetail,
    //   previousIsExploreDetail,
    //   previousIsSavedDetail,
    //   isDetailToDetailCrossSection,
    //   transitionDirection
    // });

    // Only animate on starters routes OR detail-to-detail cross-section transitions OR saved routes with transition direction OR projects routes with transition direction
    const isExploreRoute = location.pathname.startsWith('/starters');
    const isSavedRoute = location.pathname.startsWith('/saved');
  const isCommunityRoute = isCommunityList || isCommunityDetail;
    const isProjectsRoute = location.pathname.startsWith('/projects') && 
                            !location.pathname.includes('/chats/') &&
                            !location.pathname.includes('/images');
    if (!isExploreRoute && !isDetailToDetailCrossSection && (!isSavedRoute || !transitionDirection) && (!isProjectsRoute || !transitionDirection) && (!isCommunityRoute || !transitionDirection)) {
      return null; // No animations for non-starters routes unless detail-to-detail or saved/projects with direction
    }
    
    // Allow detail-to-detail animations even without transition direction
    if (!transitionDirection && !isDetailToDetailCrossSection) {
      return null; // No animation if no direction specified and not detail-to-detail
    }

    // PRIORITY: Detail-to-detail cross-section transitions (must come first!)
    if (isDetailToDetailCrossSection) {
      // Use a consistent method to determine transition direction for ALL pages
      // Look at the current page type to determine which transition is happening
      const currentIsSavedPath = location.pathname.startsWith('/saved');
      const currentIsExplorePath = location.pathname.startsWith('/starters');
      
      // SIMPLIFIED: ALL cross-section transitions use the SAME animation direction
      // Forget about trying to determine direction - just use one consistent swipe direction
      // This ensures both pages always animate in the same direction (like they're connected)
      
      const navTransitionDirection = location.state?.transitionDirection;
      
      // Use a global flag to ensure BOTH pages use the same direction for each transition
      // Determine transition type from the first page that renders
      if (!window.crossSectionDirection) {
        if (currentIsExplorePath && navTransitionDirection === 'forward') {
          window.crossSectionDirection = 'saved-to-explore';
        } else if (currentIsSavedPath && navTransitionDirection === 'back') {
          window.crossSectionDirection = 'explore-to-saved';
        }
      }
      
      // Use SAME swipe direction for ALL cross-section transitions
      // Just like /explore → /explore/detail works perfectly
      const animationConfig = {
        initial: { x: '100%', y: 0 },   // New page ALWAYS enters from right
        animate: { x: 0, y: 0 },        // Move to center
        exit: { x: '-100%', y: 0 },     // Old page ALWAYS exits to left
      };
      
      // Clear the global flag after setting animation (will be reset for next transition)
      setTimeout(() => { window.crossSectionDirection = null; }, 100);
      
      return animationConfig;
    }

    // FORWARD navigation: explore → detail
    if (transitionDirection === 'forward' && isExploreDetail) {
      return {
        initial: { x: '100%', y: 0 },
        animate: { x: 0, y: 0 },
        exit: { x: '100%', y: 0 },
      };
    }
    
    // BACK navigation: detail → explore
    if (transitionDirection === 'back' && isExplore) {
      return {
        initial: { x: '-100%', y: 0 },
        animate: { x: 0, y: 0 },
        exit: { x: '-100%', y: 0 },
      };
    }

    // SAVED route animations (detail ↔ list)
    if (isSavedRoute && transitionDirection) {
      
      if (transitionDirection === 'back') {
        // Back from detail to list - both move left-to-right (like explore)
        return {
          initial: { x: '-100%', y: 0 },  // Enter from left
          animate: { x: 0, y: 0 },        // Move to center
          exit: { x: '-100%', y: 0 },     // Exit to left
        };
      }
      
      if (transitionDirection === 'forward') {
        // Forward from list to detail - both move left-to-right
        return {
          initial: { x: '100%', y: 0 },   // Enter from right
          animate: { x: 0, y: 0 },        // Move to center
          exit: { x: '100%', y: 0 },      // Exit to right
        };
      }
    }

    // PROJECTS route animations (detail ↔ list) - same as starters/explore
    if (isProjectsRoute && transitionDirection) {
      // FORWARD navigation: projects → detail
      if (transitionDirection === 'forward' && isProjectsDetail) {
        return {
          initial: { x: '100%', y: 0 },   // Enter from right
          animate: { x: 0, y: 0 },         // Move to center
          exit: { x: '100%', y: 0 },      // Exit to right
        };
      }
      
      // BACK navigation: detail → projects
      if (transitionDirection === 'back' && isProjects) {
        return {
          initial: { x: '-100%', y: 0 },  // Enter from left
          animate: { x: 0, y: 0 },         // Move to center
          exit: { x: '-100%', y: 0 },      // Exit to left
        };
      }
    }

    // COMMUNITY route animations
    if (isCommunityRoute && transitionDirection) {
      if (transitionDirection === 'forward' && isCommunityDetail) {
        return {
          initial: { x: '100%', y: 0 },
          animate: { x: 0, y: 0 },
          exit: { x: '100%', y: 0 },
        };
      }
      if (transitionDirection === 'back' && isCommunityList) {
        return {
          initial: { x: '-100%', y: 0 },
          animate: { x: 0, y: 0 },
          exit: { x: '-100%', y: 0 },
        };
      }
    }

    if (isCommunityRoute && transitionDirection === 'back') {
      return {
        initial: { x: '-100%', y: 0 },  // Enter from left
        animate: { x: 0, y: 0 },         // Move to center
        exit: { x: '-100%', y: 0 },      // Exit to left
      };
    }

    return null;
  };

  // Check if we're transitioning between different sections (starters ↔ saved ↔ projects)
  const currentIsExplore = location.pathname.startsWith('/starters');
  const currentIsSaved = location.pathname.startsWith('/saved');
  const currentIsCommunity = isCommunityRoute;
  const currentIsProjects = location.pathname.startsWith('/projects') && 
                            !location.pathname.includes('/chats/') &&
                            !location.pathname.includes('/images');
  const previousPath = location.state?.previousPath;
  const previousIsExplore = previousPath?.startsWith('/starters');
  const previousIsSaved = previousPath?.startsWith('/saved');
  const previousIsCommunity = previousPath?.startsWith('/discover');
  const previousIsProjects = previousPath?.startsWith('/projects') && 
                             !previousPath?.includes('/chats/') &&
                             !previousPath?.includes('/images');
  
  // Detail-to-detail transitions should animate, only bypass list-to-list transitions
  const currentIsExploreDetail = isExploreDetail;
  const currentIsSavedDetail = isSavedDetail;
  const currentIsProjectsDetail = isProjectsDetail;
  const previousIsExploreDetail = previousPath?.startsWith('/starters/') && previousPath !== '/starters';
  const previousIsSavedDetail = previousPath?.startsWith('/saved/') && previousPath !== '/saved';
  const previousIsProjectsDetail = previousPath?.startsWith('/projects/') && 
                                   previousPath !== '/projects' &&
                                   !previousPath?.includes('/chats/') &&
                                   !previousPath?.includes('/images');
  
  const isDetailToDetailTransition = (currentIsExploreDetail || currentIsSavedDetail || currentIsProjectsDetail) && 
                                    (previousIsExploreDetail || previousIsSavedDetail || previousIsProjectsDetail);
  
    // Cross-section transition detection
    // Check if we're switching between different sections (starters ↔ projects ↔ saved)
  // Nav links now set previousPath, so we can reliably detect cross-section transitions
  const isCrossSectionTransition = (() => {
    if (!previousPath) {
      return false; // Can't detect without previousPath
    }
    
    // First check: are we staying within the same section? If so, NOT a cross-section transition
    const sameSection = 
      (currentIsExplore && previousIsExplore) ||
      (currentIsSaved && previousIsSaved) ||
      (currentIsProjects && previousIsProjects) ||
      (currentIsCommunity && previousIsCommunity);
    
    if (sameSection) {
      return false; // Within-section navigation - not cross-section
    }
    
    // Check if we're switching between different sections
    // Note: We include Community <-> Projects here so that list-to-list transitions are bypassed
    // Detail-to-list transitions will still animate because they have a defined 'variant'
    const explicitCrossSection = 
      (currentIsExplore && previousIsSaved) || (currentIsSaved && previousIsExplore) ||
      (currentIsExplore && previousIsProjects) || (currentIsProjects && previousIsExplore) ||
      (currentIsSaved && previousIsProjects) || (currentIsProjects && previousIsSaved) ||
      (currentIsExplore && previousIsCommunity) || (currentIsCommunity && previousIsExplore) ||
      (currentIsSaved && previousIsCommunity) || (currentIsCommunity && previousIsSaved) ||
      (currentIsProjects && previousIsCommunity) || (currentIsCommunity && previousIsProjects);
    
    // Only exclude detail-to-detail transitions (those should animate)
    // All other cross-section transitions (list-to-list, detail-to-list, list-to-detail) should bypass animation
    return explicitCrossSection && !isDetailToDetailTransition;
  })();

  const variant = getAnimationVariant();
  const component = getComponent();

  // No component for this route
  if (!component) {
    return null;
  }

  // Only bypass animations for desktop or when navigation has no state AND it's not within explore/projects section
  // IMPORTANT: Only bypass for actual cross-section transitions (different sections)
  // Within-section navigations (projects → projects/{id}) should always animate
  // CRITICAL: If we have a valid animation variant, NEVER bypass - always use animated container
  // This ensures animations work correctly after cross-section navigation
  const shouldBypassAnimation = !isMobile || 
    ((isCrossSectionTransition && !isDetailToDetailTransition) && !variant) || 
    (!previousPath && !currentIsExplore && !currentIsProjects && !currentIsCommunity);
  
  // CRITICAL FIX: If variant exists, we MUST use animated container (don't bypass)
  // This fixes the bug where animations don't work after cross-section navigation
  if (variant) {
    // Force use of animated container - don't bypass when variant exists
  } else if (shouldBypassAnimation) {
    return <Layout>{component}</Layout>;
  }
  
  // Check if this is a list page (needs scrolling) vs detail page
  const isListPage = isExplore || isSaved || isProjects || isCommunityList || isCommunityDetail;
  // The list surfaces own their internal scroll container and pin a search bar with
  // position: fixed. If the animated shell scrolls too, the whole page can slide a
  // few pixels underneath that search bar whenever Layout's pixel height and the
  // shell's 100dvh disagree - which mobile browsers do while their chrome animates.
  const ownsInternalScroll = isExplore || isSaved || isProjects || isCommunityList;
  const shouldScroll = isListPage && !ownsInternalScroll;
  
  // Always render in animated container on mobile (even without animations)
  if (!variant) {
    // Return a non-animated motion.div so AnimatePresence can track it
    return (
      <div 
        className="bg-white dark:bg-gray-900"
        style={{ 
          position: 'relative', 
          width: '100%', 
          height: '100dvh',
          overflow: 'hidden', 
        }}
      >
        <AnimatePresence 
          mode="popLayout"
          initial={false}
        >
          <motion.div
            key={location.pathname}
            initial={{ opacity: 1, y: 0 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ x: '-100%', y: 0 }}
            transition={{
              type: 'tween',
              ease: [0.4, 0, 0.2, 1],
              duration: 0.25,
            }}
            className={shouldScroll ? 'mobile-scroll' : ''}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              width: '100%',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              overflowY: shouldScroll ? 'auto' : 'hidden',
              WebkitOverflowScrolling: shouldScroll ? 'touch' : undefined,
            }}
          >
            <Layout>{component}</Layout>
          </motion.div>
        </AnimatePresence>
      </div>
    );
  }

  const transition = {
    type: 'tween',
    ease: [0.4, 0, 0.2, 1],
    duration: 0.25,
  };

  return (
    <div 
      className="bg-white dark:bg-gray-900"
      style={{ 
        position: 'relative', 
        width: '100%', 
        height: '100dvh',
        overflow: 'hidden', 
      }}
    >
      <AnimatePresence 
        mode="popLayout"
        initial={true}
      >
        <motion.div
          key={location.pathname}
          initial={variant.initial}
          animate={variant.animate}
          exit={variant.exit}
          transition={transition}
          className={shouldScroll ? 'mobile-scroll' : ''}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            width: '100%',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            overflowY: shouldScroll ? 'auto' : 'hidden',
            WebkitOverflowScrolling: shouldScroll ? 'touch' : undefined,
            willChange: 'transform',
            contain: isListPage ? 'none' : 'layout style paint',
            zIndex: 1,
            pointerEvents: 'auto',
          }}
        >
          <Layout>{component}</Layout>
        </motion.div>
      </AnimatePresence>
    </div>
  );
};

export default AnimatedRoutes;
